import * as pc from "playcanvas";
import { getArenaAABBs, ARENA_PROPS, ARENA_LAYOUT, type SurfaceMaterial } from "@hupper/shared";

const FLOOR_DIFFUSE_URL = "/textures/floor/brushed_concrete_diff_1k.jpg";
const WALL_DIFFUSE_URL  = "/textures/wall/plywood_diff_1k.jpg";
const PERIMETER_WALL_URL = "/textures/wall/plastered_wall_04_diff_1k.jpg";
const PILLAR_URL   = "/textures/metal/rusty_metal_grid_diff_1k.jpg";
const MIDFLOOR_URL = "/textures/metal/rusty_metal_04_diff_1k.jpg";
// OSB / oriented strand board — the shoot-through window walls.
const WOODWALL_URL = "/textures/wood/oriented_strand_board_diff_1k.jpg";

// SurfaceMaterial is re-exported from @hupper/shared so the client and the
// shared arena geometry stay in lockstep (incl. the "glass" member).
export type { SurfaceMaterial };

export interface AABB {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
  material?: SurfaceMaterial;
}

export interface Ramp {
  minX: number; maxX: number;
  minZ: number; maxZ: number;
  yAtMinX: number;
  yAtMaxX: number;
}

// ── Map constants — Sprint 2 (BIG VERSION) ──────────────────────────────────
const MAP_W = 80;       // X extent
const MAP_D = 50;       // Z extent
const MAP_H = 12;       // Y ceiling
const HALF_W = MAP_W / 2;
const HALF_D = MAP_D / 2;
const WALL_T = 1.0;     // perimeter wall thickness

// Catwalk
const CATWALK_Y = 3.0;        // walking surface height
const CATWALK_PLATE_T = 0.25; // slab thickness
const CATWALK_Z_N = -18;      // north strip center
const CATWALK_Z_S = +18;      // south strip center
const CATWALK_STRIP_W = 4;    // catwalk strip is 4 m wide
const CATWALK_RAIL_H = 1.2;
const CATWALK_RAIL_T = 0.2;

// Flank Routes
const FLANK_Z_N = -22;
const FLANK_Z_S = +22;
const FLANK_W = 4;

// Mid pillars
const PILLAR_T_X = -10;
const PILLAR_CT_X = +10;
const PILLAR_W = 6;

// Mid raised platform
const MID_PLAT_W = 12;
const MID_PLAT_H = 1.5;

// Locker rooms
const LOCKER_W = 8;
const LOCKER_H = 4;
const LOCKER_T_X = -35;
const LOCKER_CT_X = +35;
const LOCKER_Z = 0;

// ── Static obstacle list ─────────────────────────────────────────────────────
interface MapBlock {
  pos:  [number, number, number];
  size: [number, number, number];
  material?: SurfaceMaterial;
  tint?: [number, number, number];
  invisible?: boolean;
}

// Render meshes derive from the single data-driven ARENA_LAYOUT (shared with
// the server's collision via getArenaAABBs + read/written by the ?editor=1 map
// editor). `render` is the textured material variant ("wall"/"pillar"/
// "midfloor") when it differs from the gameplay surface; tint multiplies it.
// The old hand-listed builder lives in legacy/ArenaMapBlocks.legacy.ts.
const MAP_BLOCKS: MapBlock[] = ARENA_LAYOUT.map((b) => ({
  pos:  [b.cx, b.cy, b.cz] as [number, number, number],
  size: [b.w, b.h, b.d] as [number, number, number],
  material: b.render ?? b.surface,
  tint: b.tint,
  invisible: b.invisible,
}));

// ── Ramps ────────────────────────────────────────────────────────────────────
// Corner stairs to the catwalk were removed — no ramps in the arena now.
const MAP_RAMPS: Ramp[] = [];

// ── Spawns ───────────────────────────────────────────────────────────────────
export const SPAWNS = {
  t: [
    [-35, 0, -2.5],
    [-35, 0, 2.5],
  ] as [number, number, number][],
  ct: [
    [35, 0, -2.5],
    [35, 0, 2.5],
  ] as [number, number, number][],
};

// Texture density per render material (metres per texture repeat). Block UVs
// are generated in WORLD space (see worldUvBoxMesh) at this scale, so a box
// tiles consistently no matter its size — resizing in the editor never
// stretches it. Tuned to roughly match the previous fixed-tiling look.
const METERS_PER_TILE: Record<string, number> = {
  wall: 6, pillar: 3, midfloor: 3, woodwall: 2.5,
  concrete: 3, wood: 2, metal: 3, dirt: 3, glass: 3,
};

/**
 * Build a box mesh whose UVs are scaled by each face's WORLD dimensions, so a
 * shared, 1:1-tiled texture repeats at a constant real-world density on every
 * face for any box size. The size is baked in (mesh built at world scale), so
 * the entity must NOT be additionally scaled.
 */
function worldUvBoxMesh(device: pc.GraphicsDevice, sx: number, sy: number, sz: number, mpt: number): pc.Mesh {
  const hx = sx / 2, hy = sy / 2, hz = sz / 2;
  const positions: number[] = [], normals: number[] = [], uvs: number[] = [], indices: number[] = [];
  let base = 0;
  const face = (
    p0: number[], p1: number[], p2: number[], p3: number[],
    n: number[], uDim: number, vDim: number,
  ): void => {
    positions.push(...p0, ...p1, ...p2, ...p3);
    for (let i = 0; i < 4; i++) normals.push(...n);
    const us = uDim / mpt, vs = vDim / mpt;
    uvs.push(0, 0, us, 0, us, vs, 0, vs);
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    base += 4;
  };
  // CCW winding viewed from outside → outward-facing normals.
  face([-hx, -hy, hz], [hx, -hy, hz], [hx, hy, hz], [-hx, hy, hz], [0, 0, 1], sx, sy);   // +Z
  face([hx, -hy, -hz], [-hx, -hy, -hz], [-hx, hy, -hz], [hx, hy, -hz], [0, 0, -1], sx, sy); // -Z
  face([hx, -hy, hz], [hx, -hy, -hz], [hx, hy, -hz], [hx, hy, hz], [1, 0, 0], sz, sy);    // +X
  face([-hx, -hy, -hz], [-hx, -hy, hz], [-hx, hy, hz], [-hx, hy, -hz], [-1, 0, 0], sz, sy); // -X
  face([-hx, hy, hz], [hx, hy, hz], [hx, hy, -hz], [-hx, hy, -hz], [0, 1, 0], sx, sz);    // +Y
  face([-hx, -hy, -hz], [hx, -hy, -hz], [hx, -hy, hz], [-hx, -hy, hz], [0, -1, 0], sx, sz); // -Y
  const mesh = new pc.Mesh(device);
  mesh.setPositions(positions);
  mesh.setNormals(normals);
  mesh.setUvs(0, uvs);
  mesh.setIndices(indices);
  mesh.update();
  return mesh;
}

// ── Arena Builder ────────────────────────────────────────────────────────────
export class Arena {
  private readonly app: pc.Application;
  private readonly root: pc.Entity;
  private readonly materials = new Map<string, pc.StandardMaterial>();
  private readonly clonedMaterials: { mat: pc.StandardMaterial, type: string }[] = [];
  /** Breakable glass pane (T-room E window). Hidden when broken. */
  private glassPane: pc.Entity | null = null;

  constructor(app: pc.Application) {
    this.app = app;
    this.root = new pc.Entity("Arena");
    this.app.root.addChild(this.root);
  }

  private getMaterial(tag: SurfaceMaterial | "floor" | "ramp"): pc.StandardMaterial {
    if (this.materials.has(tag)) return this.materials.get(tag)!;
    const mat = new pc.StandardMaterial();
    mat.useLighting = true;
    mat.diffuse = new pc.Color(1, 1, 1);
    
    if (tag === "concrete" || tag === "floor") {
      mat.diffuse = new pc.Color(0.4, 0.4, 0.45);
      mat.gloss = 0.2;
      mat.metalness = 0.1;
    } else if (tag === "wood") {
      mat.diffuse = new pc.Color(0.5, 0.35, 0.2);
      mat.gloss = 0.1;
    } else if (tag === "metal") {
      mat.diffuse = new pc.Color(0.35, 0.38, 0.42);
      mat.gloss = 0.6;
      mat.metalness = 0.7;
    } else if (tag === "dirt") {
      mat.diffuse = new pc.Color(0.3, 0.25, 0.2);
      mat.gloss = 0.05;
    } else if (tag === "ramp") {
      mat.diffuse = new pc.Color(0.45, 0.45, 0.5);
      mat.gloss = 0.3;
    } else if (tag === "wall") {
      // Textured concrete wall — white diffuse so the map reads true.
      mat.diffuse = new pc.Color(1, 1, 1);
      mat.gloss = 0.3;
    } else if (tag === "pillar" || tag === "midfloor") {
      // Rusty metal — white diffuse, glossier + metallic for env reflections.
      mat.diffuse = new pc.Color(1, 1, 1);
      mat.gloss = 0.5;
      mat.useMetalness = true;
      mat.metalness = 0.6;
    } else if (tag === "woodwall") {
      // OSB wood — white diffuse so the texture reads true, matte.
      mat.diffuse = new pc.Color(1, 1, 1);
      mat.gloss = 0.12;
    }

    mat.update();
    this.materials.set(tag, mat);
    return mat;
  }

  public build(): void {
    // Load textures
    this.app.assets.loadFromUrl(FLOOR_DIFFUSE_URL, "texture", (err, asset) => {
      if (!err && asset) {
        const tex = asset.resource as pc.Texture;
        
        const floorMat = this.getMaterial("floor");
        floorMat.diffuseMap = tex;
        floorMat.diffuseMapTiling = new pc.Vec2(MAP_W / 4, MAP_D / 4);
        floorMat.update();
        
        const concreteMat = this.getMaterial("concrete");
        concreteMat.diffuseMap = tex;
        concreteMat.diffuseMapTiling = new pc.Vec2(1, 1); // world-UV mesh carries the scale
        concreteMat.update();

        const rampMat = this.getMaterial("ramp");
        rampMat.diffuseMap = tex;
        rampMat.diffuseMapTiling = new pc.Vec2(1, 1); // world-UV mesh carries the scale
        rampMat.update();

        for (const clone of this.clonedMaterials) {
          if (clone.type === "concrete" || clone.type === "floor" || clone.type === "ramp") {
            clone.mat.diffuseMap = tex;
            clone.mat.diffuseMapTiling = new pc.Vec2(1, 1); // world-UV mesh carries the scale
            clone.mat.update();
          }
        }
      }
    });

    this.app.assets.loadFromUrl(WALL_DIFFUSE_URL, "texture", (err, asset) => {
      if (!err && asset) {
        const tex = asset.resource as pc.Texture;
        
        const woodMat = this.getMaterial("wood");
        woodMat.diffuseMap = tex;
        woodMat.diffuseMapTiling = new pc.Vec2(1, 1); // world-UV mesh carries the scale
        woodMat.update();

        for (const clone of this.clonedMaterials) {
          if (clone.type === "wood") {
            clone.mat.diffuseMap = tex;
            clone.mat.diffuseMapTiling = new pc.Vec2(1, 1); // world-UV mesh carries the scale
            clone.mat.update();
          }
        }
      }
    });

    // Distinct textured surfaces (perimeter walls, mid pillars, mid floor).
    // No tint on these blocks, so the texture applies straight to the
    // shared tagged material.
    const loadTextured = (url: string, tag: SurfaceMaterial) => {
      this.app.assets.loadFromUrl(url, "texture", (err, asset) => {
        if (err || !asset) return;
        const tex = asset.resource as pc.Texture;
        const m = this.getMaterial(tag);
        m.diffuseMap = tex;
        m.diffuseMapTiling = new pc.Vec2(1, 1); // world-UV mesh carries the scale
        m.update();
      });
    };
    loadTextured(PERIMETER_WALL_URL, "wall");
    loadTextured(PILLAR_URL, "pillar");
    loadTextured(MIDFLOOR_URL, "midfloor");
    loadTextured(WOODWALL_URL, "woodwall");

    // Floor
    const floor = new pc.Entity("Floor");
    floor.addComponent("render", { type: "box", material: this.getMaterial("floor") });
    floor.setLocalScale(MAP_W, 1, MAP_D);
    floor.setLocalPosition(0, -0.5, 0);
    this.root.addChild(floor);

    // Blocks — each rendered with a world-space-UV box mesh so its texture
    // tiles at a constant real-world scale regardless of the box's dimensions
    // (the box primitive maps each face 0..1, so a fixed tiling stretches when
    // the box is resized in the editor). Size is baked into the mesh, so the
    // entity carries position only.
    const device = this.app.graphicsDevice;
    for (let i = 0; i < MAP_BLOCKS.length; i++) {
      const b = MAP_BLOCKS[i];
      const ent = new pc.Entity(`Block_${i}`);

      if (!b.invisible) {
        const tag = b.material ?? "concrete";
        let material = this.getMaterial(tag);
        if (b.tint) {
          const customMat = material.clone();
          customMat.diffuse = new pc.Color(b.tint[0], b.tint[1], b.tint[2]);
          customMat.update();
          this.clonedMaterials.push({ mat: customMat, type: tag });
          material = customMat;
        }
        const mpt = METERS_PER_TILE[tag] ?? 3;
        const mesh = worldUvBoxMesh(device, b.size[0], b.size[1], b.size[2], mpt);
        ent.addComponent("render", { meshInstances: [new pc.MeshInstance(mesh, material)] });
      }

      ent.setLocalPosition(b.pos[0], b.pos[1], b.pos[2]);
      this.root.addChild(ent);
    }

    // Breakable glass pane (server-authoritative; visual only here).
    this.buildGlass();

    // Ramps
    for (let i = 0; i < MAP_RAMPS.length; i++) {
      const r = MAP_RAMPS[i];
      const w = r.maxX - r.minX;
      const d = r.maxZ - r.minZ;
      const cx = r.minX + w / 2;
      const cz = r.minZ + d / 2;
      const hDiff = Math.abs(r.yAtMaxX - r.yAtMinX);
      const length = Math.sqrt(w * w + hDiff * hDiff);
      const angle = Math.atan2(hDiff, w) * (180 / Math.PI);
      const cy = Math.min(r.yAtMinX, r.yAtMaxX) + hDiff / 2;
      const sign = r.yAtMaxX > r.yAtMinX ? 1 : -1;

      const ent = new pc.Entity(`Ramp_${i}`);
      ent.addComponent("render", { type: "box", material: this.getMaterial("ramp") });
      ent.setLocalScale(length, 0.2, d);
      ent.setLocalPosition(cx, cy, cz);
      ent.setLocalEulerAngles(0, 0, angle * sign);
      this.root.addChild(ent);
    }

    this.buildProps();
    this.buildLighting();
  }

  /**
   * Build the translucent breakable-glass pane that fills the T-room's
   * EAST window slit. Visual only — collision / bullet / LOS blocking is
   * server-authoritative and the matching shared `GLASS_PANE_AABB` is what
   * actually stops bullets while intact (never added to getCollisionBoxes,
   * so client movement stays glass-free, matching the server). The box is
   * byte-identical to GLASS_PANE_AABB = box(-7.332, 2.725, 0, 0.8, 2.55, 4.4).
   * Hidden via {@link setGlassBroken} on the `glass_break` event.
   */
  private buildGlass(): void {
    const mat = new pc.StandardMaterial();
    mat.useLighting = true;
    mat.diffuse = new pc.Color(0.6, 0.8, 0.95);
    mat.emissive = new pc.Color(0.15, 0.25, 0.35);
    mat.opacity = 0.25;
    mat.blendType = pc.BLEND_NORMAL;
    mat.depthWrite = false;
    mat.gloss = 0.9;
    mat.metalness = 0.0;
    mat.update();

    const pane = new pc.Entity("GlassPane");
    pane.addComponent("render", { type: "box", material: mat });
    pane.setLocalScale(0.8, 2.55, 4.4);
    pane.setLocalPosition(-7.332, 2.725, 0);
    if (pane.render) pane.render.castShadows = false;
    this.root.addChild(pane);
    this.glassPane = pane;
  }

  /** Show / hide the glass pane. Driven only by the server `glass_break`
   *  event + snapshot self-heal — the client never raycasts the glass. */
  public setGlassBroken(broken: boolean): void {
    if (this.glassPane) this.glassPane.enabled = !broken;
  }

  /**
   * Load downloaded 3D-model props (covered car, …) from {@link ARENA_PROPS}
   * and place each at its world position + yaw. Collision is handled
   * separately by the shared `getArenaAABBs()` footprint boxes derived from
   * the *same* prop list, so the model the player sees and the box bullets
   * and grenades stop at always line up. Loads are async; a failed load
   * just logs and skips (the invisible collision box still stands).
   */
  private buildProps(): void {
    for (const prop of ARENA_PROPS) {
      this.app.assets.loadFromUrl(prop.modelUrl, "container", (err, asset) => {
        if (err || !asset) {
          console.warn("[Arena] prop load failed:", prop.modelUrl, err);
          return;
        }
        const container = asset.resource as unknown as {
          instantiateRenderEntity: (opts?: object) => pc.Entity;
        };
        const ent = container.instantiateRenderEntity();
        ent.name = `Prop_${prop.id}`;
        const s = prop.scale ?? 1;
        if (s !== 1) ent.setLocalScale(s, s, s);
        ent.setLocalEulerAngles(0, prop.yawDeg, 0);
        ent.setLocalPosition(prop.x, prop.y, prop.z);
        this.root.addChild(ent);
      });
    }
  }

  private buildLighting(): void {
    // Ambient
    this.app.scene.ambientLight = new pc.Color(0.15, 0.18, 0.25);

    // Skylights
    const sky1 = new pc.Entity("Skylight_1");
    sky1.addComponent("light", {
      type: "omni", color: new pc.Color(0.9, 0.95, 1.0), intensity: 1.5, range: 25, castShadows: false
    });
    sky1.setLocalPosition(-15, MAP_H - 1, 0);
    this.root.addChild(sky1);

    const sky2 = new pc.Entity("Skylight_2");
    sky2.addComponent("light", {
      type: "omni", color: new pc.Color(0.9, 0.95, 1.0), intensity: 1.5, range: 25, castShadows: false
    });
    sky2.setLocalPosition(15, MAP_H - 1, 0);
    this.root.addChild(sky2);

    // Locker Room Spotlights
    const createSpot = (x: number, z: number) => {
      const spot = new pc.Entity("LockerSpot");
      spot.addComponent("light", {
        type: "spot", color: new pc.Color(1.0, 0.8, 0.6), intensity: 2.0, range: 15, innerConeAngle: 30, outerConeAngle: 60, castShadows: false
      });
      spot.setLocalPosition(x, LOCKER_H - 0.5, z);
      spot.setLocalEulerAngles(-90, 0, 0);
      this.root.addChild(spot);
    };
    createSpot(LOCKER_T_X, LOCKER_Z);
    createSpot(LOCKER_CT_X, LOCKER_Z);
  }

  /**
   * Collision boxes for the player capsule + bullet wall tests. Returns
   * the **canonical** shared arena AABBs (`getArenaAABBs`) — the exact
   * same list the server collides against — so client prediction and
   * server authority can never disagree on where a wall is. The local
   * `MAP_BLOCKS` list is now purely for *rendering* (tints, textures)
   * and mirrors the shared geometry visually.
   * Previously this derived collision from `MAP_BLOCKS`, which had to be
   * hand-synced with the shared list and could silently drift, producing
   * "invisible wall" / "stuck where there's nothing" desyncs.
   */
  public getCollisionBoxes(): AABB[] {
    return getArenaAABBs().map(b => ({ ...b }));
  }

  public getRamps(): Ramp[] {
    return MAP_RAMPS;
  }
}
